import { useCallback, useMemo, useReducer, useRef } from 'react';

import { logger } from '@/utils/logger/logger';

import {
  DEFAULT_NESTED_PAGINATION_LOG_TAG,
  IdentifiableRow,
  SectionLoaderPayload,
  SectionPaginationApi,
  SectionRegistryResult,
} from './nested-pagination.types';

interface UseSectionRegistryParams {
  /** Replace to reset every section (callers typically pass a serialized filter). */
  resetKey: string;
  logTag?: string;
}

interface SectionState {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

/**
 * True when two row lists are equivalent for rendering: same length AND every row is the
 * SAME object reference. Reference equality (not just id equality) is deliberate — a
 * cache patch (e.g. approving a worklog) keeps the row id but produces a NEW object for the
 * changed row, and that must bump the version so the overlay re-reads it. The loader's
 * `select` keeps unchanged rows reference-stable across page appends, so an identical reload
 * still compares equal and doesn't churn.
 */
const rowsAreEquivalent = (a: IdentifiableRow[], b: IdentifiableRow[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Owns the accumulated state for ONE paginating level: a map of sectionId → loaded rows,
 * a version counter that bumps only when a section's row-identity list actually changes
 * (so identical reloads don't churn renders), per-section pagination snapshots, and the
 * set of sections still loading their first page. Compose one registry per paginating
 * level (one for each inner level of the tree) — do not configure an N-level engine.
 */
export const useSectionRegistry = <TRow extends IdentifiableRow>({
  resetKey,
  logTag = DEFAULT_NESTED_PAGINATION_LOG_TAG,
}: UseSectionRegistryParams): SectionRegistryResult<TRow> => {
  const rowsMapRef = useRef(new Map<string, TRow[]>());
  const versionRef = useRef(0);
  const sectionStateRef = useRef(new Map<string, SectionState>());
  const loadingFirstPageRef = useRef(new Set<string>());
  // Bumps whenever the first-page-loading set CHANGES (add or remove). Keyed on by the
  // `loadingSectionIds` memo — `Set.size` alone is insufficient: one section finishing while a
  // sibling starts in the same tick keeps size constant and would return a stale set.
  const loadingVersionRef = useRef(0);

  // Force a render tick when display-relevant state changes (from loader effects).
  const [, forceTick] = useReducer((n: number) => n + 1, 0);

  // Reset all sections when the reset key changes (e.g. the filter changed upstream).
  const prevResetKeyRef = useRef(resetKey);
  if (prevResetKeyRef.current !== resetKey) {
    prevResetKeyRef.current = resetKey;
    rowsMapRef.current.clear();
    sectionStateRef.current.clear();
    loadingFirstPageRef.current.clear();
    versionRef.current++;
    loadingVersionRef.current++;
  }

  const updateLoadingFirstPage = (sectionId: string, isLoadingFirstPage: boolean): boolean => {
    const set = loadingFirstPageRef.current;
    const had = set.has(sectionId);
    if (isLoadingFirstPage && !had) {
      set.add(sectionId);
      loadingVersionRef.current++;
      return true;
    }
    if (!isLoadingFirstPage && had) {
      set.delete(sectionId);
      loadingVersionRef.current++;
      return true;
    }
    return false;
  };

  const updateSectionState = (sectionId: string, payload: SectionLoaderPayload<TRow>): boolean => {
    const prev = sectionStateRef.current.get(sectionId);
    const changed =
      !prev ||
      prev.hasNextPage !== payload.hasNextPage ||
      prev.isFetchingNextPage !== payload.isFetchingNextPage;
    sectionStateRef.current.set(sectionId, {
      hasNextPage: payload.hasNextPage,
      isFetchingNextPage: payload.isFetchingNextPage,
      fetchNextPage: payload.fetchNextPage,
    });
    return changed;
  };

  const onSectionChange = useCallback(
    (sectionId: string, payload: SectionLoaderPayload<TRow>) => {
      let needsTick = false;

      // Replace + bump version when the row list changed (by length or any row reference —
      // catches both pagination appends and in-place cache patches like approve/unapprove).
      const prev = rowsMapRef.current.get(sectionId);
      if (!prev || !rowsAreEquivalent(prev, payload.rows)) {
        rowsMapRef.current.set(sectionId, payload.rows);
        versionRef.current++;
        needsTick = true;
      }

      if (updateSectionState(sectionId, payload)) needsTick = true;
      if (updateLoadingFirstPage(sectionId, payload.isLoadingFirstPage)) needsTick = true;

      logger.debug(logTag, 'section reported', {
        sectionId,
        rows: payload.rows.length,
        hasNextPage: payload.hasNextPage,
        isFetchingNextPage: payload.isFetchingNextPage,
        needsTick,
      });

      if (needsTick) forceTick();
    },
    [logTag]
  );

  // Drop a section when its loader unmounts (collapse / filter reset). Without this, a
  // loader that unmounts mid-load leaves a stuck first-page flag → perpetual spinner.
  const onSectionUnmount = useCallback((sectionId: string) => {
    let needsTick = false;
    if (rowsMapRef.current.delete(sectionId)) {
      versionRef.current++;
      needsTick = true;
    }
    sectionStateRef.current.delete(sectionId);
    if (loadingFirstPageRef.current.delete(sectionId)) {
      loadingVersionRef.current++;
      needsTick = true;
    }
    if (needsTick) forceTick();
  }, []);

  const getRows = useCallback((sectionId: string) => rowsMapRef.current.get(sectionId), []);

  const sectionPagination = useMemo<SectionPaginationApi>(
    () => ({
      getSection: sectionId => {
        const state = sectionStateRef.current.get(sectionId);
        if (!state) return undefined;
        return {
          hasNextPage: state.hasNextPage,
          isFetchingNextPage: state.isFetchingNextPage,
        };
      },
      fetchNextPage: sectionId => {
        const state = sectionStateRef.current.get(sectionId);
        logger.debug(logTag, 'fetchNextPage requested', {
          sectionId,
          hasState: !!state,
          hasNextPage: state?.hasNextPage,
          isFetchingNextPage: state?.isFetchingNextPage,
        });
        state?.fetchNextPage();
      },
    }),
    [logTag]
  );

  const loadingSectionIds = useMemo(
    () => new Set(loadingFirstPageRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingVersionRef.current]
  );

  return {
    getRows,
    version: versionRef.current,
    loadingSectionIds,
    sectionPagination,
    onSectionChange,
    onSectionUnmount,
  };
};
